import { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { colors } from "../../../../utils";
import TextView from "../../../TextView";
import { ASSETS } from "../../../../utils/config";
import { Fade, IconButton, Menu, MenuItem, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import DataBox from "../../../WheelGallery/BusinessBox/DataBox";
import { BusinessBoxDefaultData } from "../../../WheelGallery/BusinessBox";
import { useDispatch, useSelector } from "react-redux";
import { getWheelBusinessBox } from "../../../../redux/selectors/segmentBuilderSelectors";
import { isEmpty } from "lodash";
import { SocialIcons } from "../../../../utils/socialHelpers";
import { FaEllipsisV } from "react-icons/fa";
import Edit from "@assets/images/editIcon.svg";
import LoopIcon from "@mui/icons-material/Loop";
import { useIfUserEligible } from "../../../../hooks/useIfUserEligible";
import { FEATURES } from "../../../../constants/features";
import { ReactComponent as Premium } from "@assets/images/PremiumSubscriptionIcon.svg";
import { CircularProgress } from "@mui/material";
import FeatureEligibility from "../../../FeatureEligibility";
import { openUpgradePopup } from "../../../../redux/reducers/upgradePopup";
import { StyledTooltip } from "../../../IyugaTooltip";
import { useGetBusinessBoxV2 } from "../../../../services/mutations";
import { getLoggedInUser } from "../../../../utils/helpers";

// Define animations for viewWheel mode
const slideIn = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`;

const Container = styled.div`
  ${({ isViewWheel, isVisible }) => {
    if (isViewWheel) {
      return css`
        width: 25vw;
        height: 100vh;
        position: absolute;
        left: 0;
        top: 0;
        background-color: #fff;
        box-shadow: 2px 0 5px rgba(0, 0, 0, 0.2);
        z-index: 1000;
        border-top-right-radius: 12px;
        border-bottom-right-radius: 12px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        /* Animate in/out */
        animation: ${isVisible ? slideIn : slideOut} 0.3s ease-in-out forwards;

        /* Hide scroll bar for the entire container */
        ::-webkit-scrollbar {
          display: none;
        }
        -ms-overflow-style: none;
        scrollbar-width: none;
      `;
    } else {
      return css`
        min-width: 25vw;
        background-color: white;
        padding: 0;
        box-shadow: 0px 0px 9.3px 0px #3b3b3b1c;
        height: 92vh;
        display: flex;
        flex-direction: column;
        transform: translateX(100%);
        transition: transform 0.3s ease-in-out;

        ${isVisible &&
        css`
          transform: translateX(1px);
        `}
      `;
    }
  }}
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 24px 16px 10px 16px;
  margin-bottom: 10px;
  border-bottom: 1px solid ${colors.lightBorder};
  justify-content: space-between;
  background-color: white;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
`;

const BusinessBoxBuilder = ({
  isVisible,
  onClose,
  onEditClick,
  isViewWheel,
  onReplaceClick,
}) => {
  const dispatch = useDispatch();
  const bboxData = useSelector(getWheelBusinessBox);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Add state to store all business boxes
  const [allBusinessBoxes, setAllBusinessBoxes] = useState([]);
  const [isLoadingBoxes, setIsLoadingBoxes] = useState(false);

  const getBusinessBox = useGetBusinessBoxV2();

  // Function to fetch all business boxes
  const fetchBusinessBoxes = () => {
    setIsLoadingBoxes(true);
    const userId = getLoggedInUser()?.id;

    getBusinessBox.mutate(
      { userid: userId },
      {
        onSuccess: (response) => {
          if (response?.status === 200) {
            setAllBusinessBoxes(response?.data?.results || []);
          } else {
            console.error("Failed to fetch business boxes:", response);
          }
          setIsLoadingBoxes(false);
        },
        onError: (err) => {
          console.error("Error fetching business boxes:", err);
          setIsLoadingBoxes(false);
        },
      }
    );
  };

  // Fetch business boxes when component mounts
  useEffect(() => {
    fetchBusinessBoxes();
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const {
    isEligible,
    subscription,
    isCheckingEligibility,
    requiredSubscription,
  } = useIfUserEligible(FEATURES.BUSINESS_BOX);

  const handlePremiumClick = () => {
    dispatch(
      openUpgradePopup({
        requiredSubscription: 2,
      })
    );
  };

  // Check if current bboxData exists in the business boxes array
  const isBboxInCollection = () => {
    if (isEmpty(bboxData) || !bboxData.id || allBusinessBoxes.length === 0) {
      return false;
    }

    return allBusinessBoxes.some((box) => box.id === bboxData.id);
  };

  return (
    <Container isVisible={isVisible} isViewWheel={isViewWheel}>
      <Header>
        <Stack direction={"row"}>
          <img src={ASSETS.segmentEditor.Business} style={{ marginRight: 5 }} />
          <TextView variant="title" style={{ height: "100%" }}>
            Business Box
          </TextView>
        </Stack>

        {isViewWheel ? (
          <CloseIcon
            sx={{ cursor: "pointer", color: "#AAABAD" }}
            onClick={onClose}
          />
        ) : !isEligible ? (
          <StyledTooltip
            title={"Premium Tier feature"}
            arrow
            placement="top"
            color={"#9256E7"}
            textColor={"#fff"}
          >
            <Premium
              style={{ width: "24px", height: "24px", cursor: "pointer" }}
              onClick={handlePremiumClick}
            />
          </StyledTooltip>
        ) : (
          <div
            style={{
              width: "30px",
            }}
          >
            <IconButton
              style={{ color: "#123051", width: "24px", height: "24px" }}
              onClick={(event) => {
                setAnchorEl(event.currentTarget);
              }}
            >
              <FaEllipsisV
                color="#565556"
                style={{ cursor: "pointer", width: "8px", height: "16px" }}
              />
            </IconButton>

            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              TransitionComponent={Fade}
              timeout={{ enter: 100, exit: 300 }}
              PaperProps={{
                style: {
                  borderRadius: "8px",
                  width: "100px",
                  transition: "box-shadow 0.3s ease-in-out",
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  onEditClick();
                  handleClose();
                }}
                sx={{
                  fontSize: "14px",
                  color: "#333",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                <img
                  style={{ marginRight: "0.2rem" }}
                  src={Edit}
                  className="img-fluid"
                  alt="Edit"
                  width="15"
                  height="15"
                />
                Edit
              </MenuItem>

              <MenuItem
                onClick={() => {
                  onReplaceClick();
                  handleClose();
                }}
                sx={{
                  fontSize: "14px",
                  color: "#333",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                <LoopIcon
                  sx={{
                    marginRight: "0.2rem",
                    width: "18px",
                    height: "18px",
                    color: "#BBBBBB",
                  }}
                />
                Replace
              </MenuItem>
            </Menu>
          </div>
        )}
      </Header>

      <Content>
        {isCheckingEligibility || isLoadingBoxes ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <CircularProgress />
          </div>
        ) : !isEligible ? (
          <FeatureEligibility
            subscription={"Premium"}
            requiredSubscription={2}
          />
        ) : // Only render actual content after eligibility and data loading are confirmed
        !isEmpty(bboxData) && (isViewWheel ? isBboxInCollection() : true) ? (
          <DataBox
            data={{
              business_title: bboxData.business_title,
              imageSrc: bboxData.media_url,
              description: bboxData.description,
              icons:
                bboxData.business_link?.map((linkItem, idx) => {
                  return {
                    id: idx,
                    icon: linkItem.icon,
                    link: linkItem.link,
                    is_cta_enabled: linkItem.is_cta_enabled,
                  };
                }) || [],
              ctaButtons: bboxData.business_box_cta || [],
              id: bboxData.id || null,
            }}
            isFormPreview
          />
        ) : (
          <DataBox data={BusinessBoxDefaultData} isDefault />
        )}
      </Content>
    </Container>
  );
};

export default BusinessBoxBuilder;
